import { supabase } from '../lib/supabase'
import { recalculateScore } from '../lib/governanceScore'
import { logAuditEvent } from '../lib/audit'
import { uploadFile, STORAGE_LIMITS } from '../lib/storage'

// src/lib/api.js - Add rate limiting wrapper
import { checkRateLimit } from './security/rate-limit'

async function rateLimitedRequest(action, identifier, fn) {
  const result = checkRateLimit(action, identifier)
  if (!result.allowed) {
    throw new Error(result.reason || 'Rate limit exceeded')
  }
  return fn()
}

function handleError(error, fallback = 'Operation failed') {
  const msg = error?.message || fallback
  return { error: msg, data: null }
}

// ============================================================
// COMPANIES API
// ============================================================

export const companiesApi = {
  async list() {
    const { data, error } = await supabase.from('companies').select('*').order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async listPublic() {
    const { data, error } = await supabase
      .from('companies')
      .select('id, name, slug, country, currency, industry, governance_score, readiness_score')
      .eq('public_visibility', true)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async get(id) {
    const { data, error } = await supabase.from('companies').select('*').eq('id', id).maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  async getBySlug(slug) {
    const { data, error } = await supabase
      .from('companies')
      .select('id, name, slug, country, currency, industry, governance_score, readiness_score')
      .eq('slug', slug)
      .eq('public_visibility', true)
      .maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  async create(payload) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return handleError({ message: 'Not authenticated' })
    const { data, error } = await supabase
      .from('companies')
      .insert({ ...payload, owner_id: user.id })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async update(id, payload) {
    const { data, error } = await supabase
      .from('companies')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async toggleVisibility(id) {
    const { data: current } = await supabase.from('companies').select('public_visibility').eq('id', id).maybeSingle()
    const newVal = !current?.public_visibility
    const { data, error } = await supabase
      .from('companies')
      .update({ public_visibility: newVal, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async remove(id) {
    const { error } = await supabase.from('companies').delete().eq('id', id)
    return error ? handleError(error) : { error: null }
  },
}

// ============================================================
// DOCUMENTS API
// ============================================================

export const documentsApi = {
  // ✅ FIX: Add company membership validation
  async _checkCompanyAccess(companyId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single()
    
    // Admin can access all
    if (profile?.role === 'admin') return true
    
    // Check if user belongs to this company
    if (profile?.company_id !== companyId) {
      throw new Error('Unauthorized access to this company\'s documents')
    }
    return true
  },

  async list(companyId) {
    // ✅ Add validation
    await this._checkCompanyAccess(companyId)
    
    const { data, error } = await supabase
      .from('governance_documents')
      .select('*')
      .eq('company_id', companyId)
      .order('category', { ascending: true })
    return error ? handleError(error) : { data, error: null }
  },

  async queue() {
    const { data, error } = await supabase
      .from('governance_documents')
      .select('*, companies!inner(name)')
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async get(id) {
    const { data, error } = await supabase.from('governance_documents').select('*').eq('id', id).maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ UPDATED: Upload with comprehensive file validation + role detection
  async upload(file, { company_id, label, category, uploaded_by }) {
    // ============================================================
    // FILE VALIDATION - Server-side security checks
    // ============================================================
    await this._checkCompanyAccess(company_id)
    
    // 1. Check if file exists
    if (!file) {
      return { data: null, error: { message: 'No file provided' } }
    }

    // 2. Check file size
    if (file.size > STORAGE_LIMITS.MAX_FILE_SIZE) {
      return { 
        data: null, 
        error: { 
          message: `File size exceeds ${STORAGE_LIMITS.MAX_FILE_SIZE_MB}MB limit. Please compress or split the file.` 
        } 
      }
    }

    // 3. Check if file is empty
    if (file.size === 0) {
      return { data: null, error: { message: 'File is empty' } }
    }

    // 4. Check file extension
    const fileExt = '.' + file.name.split('.').pop().toLowerCase()
    if (!STORAGE_LIMITS.ALLOWED_EXTENSIONS.includes(fileExt)) {
      return { 
        data: null, 
        error: { 
          message: `File type "${fileExt}" is not allowed. Allowed: ${STORAGE_LIMITS.ALLOWED_EXTENSIONS.join(', ')}` 
        } 
      }
    }

    // 5. Check MIME type
    if (!STORAGE_LIMITS.ALLOWED_TYPES.includes(file.type)) {
      return { 
        data: null, 
        error: { 
          message: `File type "${file.type}" is not allowed.` 
        } 
      }
    }

    // 6. Check for dangerous file names (path traversal, etc.)
    const dangerousPatterns = ['..', '/', '\\', '%00', ';', '|', '&', '$', '(', ')', '`', '>', '<']
    for (const pattern of dangerousPatterns) {
      if (file.name.includes(pattern)) {
        return { 
          data: null, 
          error: { 
            message: 'File name contains invalid characters' 
          } 
        }
      }
    }

    // 7. ✅ Check file content (PDF header, image magic numbers)
    try {
      const buffer = await file.arrayBuffer()
      const bytes = new Uint8Array(buffer)
      
      // Check PDF files
      if (file.type === 'application/pdf') {
        const pdfHeader = [0x25, 0x50, 0x44, 0x46] // %PDF
        for (let i = 0; i < 4; i++) {
          if (bytes[i] !== pdfHeader[i]) {
            return { 
              data: null, 
              error: { 
                message: 'Invalid PDF file format' 
              } 
            }
          }
        }
      }
      
      // Check JPEG images
      if (file.type === 'image/jpeg') {
        if (bytes[0] !== 0xFF || bytes[1] !== 0xD8 || bytes[2] !== 0xFF) {
          return { 
            data: null, 
            error: { 
              message: 'Invalid JPEG file format' 
            } 
          }
        }
      }
      
      // Check PNG images
      if (file.type === 'image/png') {
        const pngHeader = [0x89, 0x50, 0x4E, 0x47]
        for (let i = 0; i < 4; i++) {
          if (bytes[i] !== pngHeader[i]) {
            return { 
              data: null, 
              error: { 
                message: 'Invalid PNG file format' 
              } 
            }
          }
        }
      }
      
      // Check GIF images
      if (file.type === 'image/gif') {
        const gifHeader = [0x47, 0x49, 0x46, 0x38]
        for (let i = 0; i < 4; i++) {
          if (bytes[i] !== gifHeader[i]) {
            return { 
              data: null, 
              error: { 
                message: 'Invalid GIF file format' 
              } 
            }
          }
        }
      }
    } catch (err) {
      return { 
        data: null, 
        error: { 
          message: 'Failed to read file content' 
        } 
      }
    }

    // ============================================================
    // FILE UPLOAD - Proceed with upload
    // ============================================================

    const { data: { user } } = await supabase.auth.getUser()
    
    // ✅ Get user role to determine document status
    const { data: profile } = await supabase
      .from('profiles')
      .select('role, full_name')
      .eq('id', user?.id)
      .maybeSingle()

    // ✅ Set status based on role
    // Staff uploads → 'pending_founder_review'
    // Founder uploads → 'uploaded'
    const status = profile?.role === 'staff' 
      ? 'pending_founder_review' 
      : 'uploaded'

    // Sanitize file name
    const sanitizedName = (file?.name || `${label.toLowerCase().replace(/\s+/g, '-')}.pdf`)
      .replace(/[^a-zA-Z0-9.\s-]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .substring(0, 200)

    // Add timestamp to prevent collisions
    const timestamp = Date.now()
    const finalFileName = `${timestamp}-${sanitizedName}`

    try {
      const { path } = await uploadFile(file, company_id, category, finalFileName)
      
      const { data, error } = await supabase
        .from('governance_documents')
        .insert({
          company_id,
          label,
          category,
          status: status, // ✅ Dynamic status based on role
          file_url: path,
          file_name: sanitizedName,
          uploaded_by: uploaded_by || profile?.full_name || 'Founder',
          uploaded_by_id: user?.id,
          uploaded_by_role: profile?.role || 'founder', // ✅ Track who uploaded
        })
        .select()
        .single()
        
      if (!error) {
        await recalculateScore(company_id)
        
        // ✅ Add activity stamp for upload
        await activityApi.addActivity({
          companyId: company_id,
          actorId: user?.id,
          actorName: profile?.full_name || uploaded_by || 'User',
          actorRole: profile?.role || 'founder',
          actionType: 'document_uploaded',
          actionDetails: `Uploaded ${label} (${(file.size / 1024).toFixed(1)}KB)`,
          entityType: 'document',
          entityId: data.id,
          targetId: company_id,
        })

        await logAuditEvent({ 
          company_id, 
          document_id: data.id, 
          action: 'Uploaded', 
          actor: uploaded_by || 'Founder', 
          actor_id: user?.id,
          details: `File: ${sanitizedName}, Size: ${(file.size / 1024).toFixed(1)}KB`
        })
      }
      
      return error ? handleError(error) : { data, error: null }
      
    } catch (err) {
      return { 
        data: null, 
        error: { 
          message: err.message || 'Upload failed. Please try again.' 
        } 
      }
    }
  },

  async updateStatus(id, status, extra = {}) {
    const { data, error } = await supabase
      .from('governance_documents')
      .update({ status, ...extra, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (!error && data?.company_id) {
      await recalculateScore(data.company_id)
      await logAuditEvent({ 
        company_id: data.company_id, 
        document_id: id, 
        action: `Status changed to ${status}`, 
        details: extra.reject_reason 
      })
      
      // ✅ Add activity stamp for status change
      await activityApi.addActivity({
        companyId: data.company_id,
        actorId: null,
        actorName: 'System',
        actorRole: 'system',
        actionType: 'status_changed',
        actionDetails: `Document status changed to ${status}`,
        entityType: 'document',
        entityId: id,
        targetId: data.company_id,
      })
    }
    return error ? handleError(error) : { data, error: null }
  },

  async verify(id) {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('governance_documents')
      .update({ 
        status: 'verified', 
        verified_by: user?.id, 
        verified_at: new Date().toISOString(), 
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single()
    if (!error && data?.company_id) {
      await recalculateScore(data.company_id)
      await logAuditEvent({ 
        company_id: data.company_id, 
        document_id: id, 
        action: 'Platform Verified', 
        actor: 'Platform Admin', 
        actor_id: user?.id 
      })
      
      await activityApi.addActivity({
        companyId: data.company_id,
        actorId: user?.id,
        actorName: 'Platform Admin',
        actorRole: 'admin',
        actionType: 'document_approved',
        actionDetails: `Verified ${data.label}`,
        entityType: 'document',
        entityId: id,
        targetId: data.company_id,
      })
    }
    return error ? handleError(error) : { data, error: null }
  },

  async reject(id, reason) {
    const { data, error } = await supabase
      .from('governance_documents')
      .update({ 
        status: 'rejected', 
        reject_reason: reason, 
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single()
    if (!error && data?.company_id) {
      await recalculateScore(data.company_id)
      await logAuditEvent({ 
        company_id: data.company_id, 
        document_id: id, 
        action: 'Rejected', 
        details: reason 
      })
      
      await activityApi.addActivity({
        companyId: data.company_id,
        actorId: null,
        actorName: 'Platform Admin',
        actorRole: 'admin',
        actionType: 'document_rejected',
        actionDetails: `Rejected ${data.label}: ${reason}`,
        entityType: 'document',
        entityId: id,
        targetId: data.company_id,
      })
    }
    return error ? handleError(error) : { data, error: null }
  },

  async remove(id) {
    const { data } = await supabase.from('governance_documents').select('company_id').eq('id', id).maybeSingle()
    const { error } = await supabase.from('governance_documents').delete().eq('id', id)
    if (!error && data?.company_id) {
      await recalculateScore(data.company_id)
      await logAuditEvent({ 
        company_id: data.company_id, 
        document_id: id, 
        action: 'Deleted' 
      })
    }
    return error ? handleError(error) : { error: null }
  },

  async getScore(companyId) {
    const { data } = await supabase.from('governance_documents').select('status, category').eq('company_id', companyId)
    return { data, error: null }
  },

  // ✅ NEW: Get documents pending founder review (staff uploads)
  async getPendingFounderReview(companyId) {
    const { data, error } = await supabase
      .from('governance_documents')
      .select('*')
      .eq('company_id', companyId)
      .eq('status', 'pending_founder_review')
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ NEW: Staff can only see their own documents
  async listForStaff(companyId, userId) {
    const { data, error } = await supabase
      .from('governance_documents')
      .select('*')
      .eq('company_id', companyId)
      .eq('uploaded_by_id', userId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ NEW: Founder approves staff document
  async approveStaffDocument(id) {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('governance_documents')
      .update({ 
        status: 'uploaded', 
        founder_approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single()
      
    if (!error && data?.company_id) {
      await recalculateScore(data.company_id)
      await logAuditEvent({ 
        company_id: data.company_id, 
        document_id: id, 
        action: 'Founder Approved Staff Upload',
        details: `Document approved by founder for verification`
      })
      
      await activityApi.addActivity({
        companyId: data.company_id,
        actorId: user?.id,
        actorName: 'Founder',
        actorRole: 'founder',
        actionType: 'document_approved',
        actionDetails: `Approved ${data.label} for platform verification`,
        entityType: 'document',
        entityId: id,
        targetId: data.uploaded_by_id,
      })
    }
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ NEW: Founder rejects staff document
  async rejectStaffDocument(id, reason) {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('governance_documents')
      .update({ 
        status: 'rejected', 
        reject_reason: reason,
        founder_rejected_at: new Date().toISOString(),
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single()
      
    if (!error && data?.company_id) {
      await activityApi.addActivity({
        companyId: data.company_id,
        actorId: user?.id,
        actorName: 'Founder',
        actorRole: 'founder',
        actionType: 'document_rejected',
        actionDetails: `Rejected ${data.label}: ${reason}`,
        entityType: 'document',
        entityId: id,
        targetId: data.uploaded_by_id,
      })
    }
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// DOCUMENT PERMISSIONS API
// ============================================================

export const documentPermissionsApi = {
  // ✅ Grant permission to staff
  async grantPermission(documentId, staffId, permissionType, grantedBy) {
    const { data, error } = await supabase
      .from('document_permissions')
      .insert({
        document_id: documentId,
        staff_id: staffId,
        permission_type: permissionType,
        granted_by: grantedBy,
      })
      .select()
      .single()
    
    if (!error) {
      // Get document details for activity
      const { data: doc } = await supabase
        .from('governance_documents')
        .select('company_id, label')
        .eq('id', documentId)
        .single()
      
      // Get staff details
      const { data: staff } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', staffId)
        .single()
      
      if (doc) {
        await activityApi.addActivity({
          companyId: doc.company_id,
          actorId: grantedBy,
          actorName: 'Founder',
          actorRole: 'founder',
          actionType: 'permission_granted',
          actionDetails: `Granted ${permissionType} permission to ${staff?.full_name || staffId} for ${doc.label}`,
          entityType: 'document',
          entityId: documentId,
          targetId: staffId,
        })
      }
    }
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Revoke permission
  async revokePermission(documentId, staffId, permissionType) {
    const { error } = await supabase
      .from('document_permissions')
      .delete()
      .eq('document_id', documentId)
      .eq('staff_id', staffId)
      .eq('permission_type', permissionType)
    return error ? handleError(error) : { error: null }
  },

  // ✅ Check if staff has permission
  async checkPermission(documentId, staffId, permissionType) {
    const { data, error } = await supabase
      .from('document_permissions')
      .select('*')
      .eq('document_id', documentId)
      .eq('staff_id', staffId)
      .eq('permission_type', permissionType)
      .maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get all permissions for a document
  async getDocumentPermissions(documentId) {
    const { data, error } = await supabase
      .from('document_permissions')
      .select('*, profiles(full_name, email, role)')
      .eq('document_id', documentId)
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get all documents staff can access
  async getStaffAccessibleDocuments(staffId) {
    const { data, error } = await supabase
      .from('document_permissions')
      .select('document_id, permission_type, governance_documents(*)')
      .eq('staff_id', staffId)
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// ACTIVITY FEED API
// ============================================================

export const activityApi = {
  // ✅ Add activity stamp
  async addActivity({ companyId, actorId, actorName, actorRole, actionType, actionDetails, entityType, entityId, targetId }) {
    const { data, error } = await supabase
      .from('activity_feed')
      .insert({
        company_id: companyId,
        actor_id: actorId,
        actor_name: actorName,
        actor_role: actorRole,
        action_type: actionType,
        action_details: actionDetails,
        entity_type: entityType,
        entity_id: entityId,
        target_id: targetId,
      })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get company activity feed
  async getCompanyActivity(companyId, limit = 50) {
    const { data, error } = await supabase
      .from('activity_feed')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(limit)
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get activity for a specific document
  async getDocumentActivity(documentId) {
    const { data, error } = await supabase
      .from('activity_feed')
      .select('*')
      .eq('entity_id', documentId)
      .eq('entity_type', 'document')
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// TEAM CHAT API
// ============================================================

export const teamChatApi = {
  // ✅ Send team message
  async sendMessage({ companyId, documentId, senderId, senderName, senderRole, message, isPublic = true }) {
    const { data, error } = await supabase
      .from('team_messages')
      .insert({
        company_id: companyId,
        document_id: documentId,
        sender_id: senderId,
        sender_name: senderName,
        sender_role: senderRole,
        message,
        is_public: isPublic,
      })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get document messages
  async getDocumentMessages(documentId) {
    const { data, error } = await supabase
      .from('team_messages')
      .select('*')
      .eq('document_id', documentId)
      .order('created_at', { ascending: true })
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get public team messages (all staff)
  async getPublicMessages(companyId, documentId) {
    const { data, error } = await supabase
      .from('team_messages')
      .select('*')
      .eq('company_id', companyId)
      .eq('document_id', documentId)
      .eq('is_public', true)
      .order('created_at', { ascending: true })
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// AGENTS API
// ============================================================

export const agentsApi = {
  async list() {
    const { data, error } = await supabase.from('agent_partners').select('*').order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async create(payload) {
    const { data, error } = await supabase.from('agent_partners').insert(payload).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async update(id, payload) {
    const { data, error } = await supabase.from('agent_partners').update(payload).eq('id', id).select().single()
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// AGENT REQUESTS API
// ============================================================

export const agentRequestsApi = {
  async list() {
    const { data, error } = await supabase
      .from('agent_requests')
      .select('*, companies!inner(name), lawyers(name)')
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async listForCompany(companyId) {
    const { data, error } = await supabase
      .from('agent_requests')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async create(payload) {
    const { data, error } = await supabase.from('agent_requests').insert(payload).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async update(id, payload) {
    const { data, error } = await supabase.from('agent_requests').update(payload).eq('id', id).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async assign(id, agentId) {
    const { data, error } = await supabase
      .from('agent_requests')
      .update({ agent_id: agentId, status: 'Assigned', assigned_date: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async uploadDraft(id) {
    const { data, error } = await supabase
      .from('agent_requests')
      .update({ status: 'Client Review', draft_uploaded_date: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async approve(id) {
    const { data, error } = await supabase
      .from('agent_requests')
      .update({ status: 'Finalized', client_approved_date: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// ACCESS REQUESTS API
// ============================================================

export const accessRequestsApi = {
  async list(companyId) {
    const { data, error } = await supabase
      .from('access_requests')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async listAll() {
    const { data, error } = await supabase
      .from('access_requests')
      .select('*, companies!inner(name)')
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async create(payload) {
    const { data, error } = await supabase.from('access_requests').insert(payload).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async approve(id) {
    const { data, error } = await supabase
      .from('access_requests')
      .update({ status: 'Approved', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async deny(id) {
    const { data, error } = await supabase
      .from('access_requests')
      .update({ status: 'Denied', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async setStatus(id, status, accountStatus) {
    const payload = { status }
    if (accountStatus) payload.account_status = accountStatus
    const { data, error } = await supabase
      .from('access_requests')
      .update(payload)
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// VAULT API
// ============================================================

export const vaultApi = {
  async listTokens(companyId) {
    const { data, error } = await supabase
      .from('investor_tokens')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async createToken(companyId, investorEmail, investorName) {
    const { data, error } = await supabase
      .from('investor_tokens')
      .insert({ company_id: companyId, investor_email: investorEmail, investor_name: investorName })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async revokeToken(id) {
    const { data, error } = await supabase
      .from('investor_tokens')
      .update({ status: 'revoked' })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async validateToken(token) {
    const { data, error } = await supabase
      .from('investor_tokens')
      .select('*, companies(*)')
      .eq('token', token)
      .eq('status', 'active')
      .maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  async getVerifiedDocs(companyId) {
    const { data, error } = await supabase
      .from('governance_documents')
      .select('*')
      .eq('company_id', companyId)
      .in('status', ['verified', 'awaiting'])
      .order('category', { ascending: true })
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// SUBSCRIPTIONS API
// ============================================================

export const subscriptionsApi = {
  async get(companyId) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  async createOrUpgrade(companyId, plan) {
    const { data: existing } = await supabase.from('subscriptions').select('id').eq('company_id', companyId).maybeSingle()
    if (existing) {
      const { data, error } = await supabase
        .from('subscriptions')
        .update({ plan, status: 'active', renewal_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0] })
        .eq('id', existing.id)
        .select()
        .single()
      return error ? handleError(error) : { data, error: null }
    }
    const { data, error } = await supabase
      .from('subscriptions')
      .insert({ company_id: companyId, plan, status: 'active', renewal_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0] })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async cancel(id) {
    const { data, error } = await supabase
      .from('subscriptions')
      .update({ status: 'canceled' })
      .eq('id', id)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async list() {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*, companies!inner(name)')
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// TOKENS API
// ============================================================

export const tokensApi = {
  async getWallet(companyId) {
    const { data, error } = await supabase
      .from('token_purchases')
      .select('token_count, status')
      .eq('company_id', companyId)
      .eq('status', 'completed')
    if (error) return handleError(error)
    const available = data?.reduce((sum, r) => sum + r.token_count, 0) || 0
    return { data: { available }, error: null }
  },

  async purchase(companyId, tokenCount, amount) {
    const { data, error } = await supabase
      .from('token_purchases')
      .insert({ company_id: companyId, token_count: tokenCount, amount, status: 'completed' })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// INVOICES API
// ============================================================

export const invoicesApi = {
  async list(companyId) {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// STAFF API
// ============================================================

export const staffApi = {
  async list(companyId) {
    const { data, error } = await supabase
      .from('staff')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async create(payload) {
    const { data, error } = await supabase.from('staff').insert(payload).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async update(id, payload) {
    const { data, error } = await supabase.from('staff').update(payload).eq('id', id).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async remove(id) {
    const { error } = await supabase.from('staff').delete().eq('id', id)
    return error ? handleError(error) : { error: null }
  },
}

// ============================================================
// PROFILES API
// ============================================================

export const profilesApi = {
  async get(userId) {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
    return error ? handleError(error) : { data, error: null }
  },

  async update(userId, payload) {
    const { data, error } = await supabase.rpc('update_profile_safe', {
      p_full_name: payload.full_name ?? null,
      p_phone: payload.phone ?? null,
      p_id_verification: payload.id_verification ?? null,
      p_company_id: payload.company_id ?? null,
    })
    if (error) return handleError(error)
    const { data: fresh, error: fetchErr } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
    return fetchErr ? handleError(fetchErr) : { data: fresh, error: null }
  },

  async create(payload) {
    const { data, error } = await supabase.from('profiles').insert(payload).select().single()
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// AGENTS LEGACY API
// ============================================================

export const agentsLegacyApi = {
  async list() {
    const { data, error } = await supabase.from('lawyers').select('*').order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },

  async invite(payload) {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('lawyers')
      .insert({ ...payload, invited_by: user?.id, status: 'invited' })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  async update(id, payload) {
    const { data, error } = await supabase.from('lawyers').update(payload).eq('id', id).select().single()
    return error ? handleError(error) : { data, error: null }
  },

  async remove(id) {
    const { error } = await supabase.from('lawyers').delete().eq('id', id)
    return error ? handleError(error) : { error: null }
  },
}

// ============================================================
// COMPANIES VERIFICATION API
// ============================================================

export const companiesVerificationApi = {
  async setStatus(companyId, status) {
    const { data, error } = await supabase
      .from('companies')
      .update({ verification_status: status, updated_at: new Date().toISOString() })
      .eq('id', companyId)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },
}

// ============================================================
// PAYMENTS API
// ============================================================

export const paymentsApi = {
  async initialize(plan, companyId, callbackUrl) {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return { data: null, error: { message: 'Not authenticated' } }
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paystack-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ plan, company_id: companyId, callback_url: callbackUrl }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Request failed' }))
      return { data: null, error: err }
    }
    const data = await res.json()
    return { data, error: null }
  },

  async list(companyId) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
    return error ? handleError(error) : { data, error: null }
  },
}

// Add this to src/lib/api.js

// ============================================================
// COUNTRY SUPPORT API
// ============================================================

export const countryApi = {
  // Get country-specific documents
  async getCountryDocuments(country) {
    const { data, error } = await supabase
      .from('country_documents')
      .select('*')
      .eq('country', country)
      .order('category', { ascending: true })
    return error ? handleError(error) : { data, error: null }
  },

  // Get user's country
  async getUserCountry(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('country')
      .eq('id', userId)
      .maybeSingle()
    return error ? handleError(error) : { data: data?.country || 'Nigeria', error: null }
  },

  // Update user's country
  async updateUserCountry(userId, country) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ country })
      .eq('id', userId)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },
}

// Add this after the existing APIs in src/lib/api.js

// ============================================================
// AGENT MARKETPLACE API
// ============================================================

export const agentMarketplaceApi = {
  // ✅ Get all available agents (for founders)
  async getAvailableAgents(country = null, specialty = null) {
    let query = supabase
      .from('lawyers')
      .select('*')
      .eq('is_available', true)
      .eq('verification_status', 'verified')
      .order('rating', { ascending: false })

    if (country) {
      query = query.contains('countries', [country])
    }
    if (specialty) {
      query = query.contains('specialties', [specialty])
    }

    const { data, error } = await query
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Register as an agent (lawyer)
  async register(payload) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return handleError({ message: 'Not authenticated' })

    const { data, error } = await supabase
      .from('lawyers')
      .insert({
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        country: payload.country,
        countries: payload.countries || [payload.country],
        languages: payload.languages || ['English'],
        bio: payload.bio,
        experience_years: parseInt(payload.experience_years) || 0,
        specialties: payload.specialties || [],
        hourly_rate: parseFloat(payload.hourly_rate) || 50,
        currency: payload.currency || 'USD',
        verification_documents: payload.verification_documents || {},
        invited_by: user.id,
        status: 'invited',
        verification_status: 'pending',
        is_available: true,
      })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Apply for a project (agent applies to company)
  async applyToProject(lawyerId, companyId, documentName, message) {
    const { data, error } = await supabase
      .from('agent_applications')
      .insert({
        lawyer_id: lawyerId,
        company_id: companyId,
        document_name: documentName,
        message,
        status: 'pending',
      })
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Company reviews applications
  async reviewApplication(applicationId, status, reviewedBy) {
    const { data, error } = await supabase
      .from('agent_applications')
      .update({
        status,
        reviewed_at: new Date().toISOString(),
        reviewed_by: reviewedBy,
      })
      .eq('id', applicationId)
      .select()
      .single()
    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Assign agent to a project (prevents double-booking)
  async assignAgentToProject(agentRequestId, lawyerId) {
    // Check if agent is available
    const { data: lawyer } = await supabase
      .from('lawyers')
      .select('is_available, current_project_id')
      .eq('id', lawyerId)
      .single()

    if (!lawyer || !lawyer.is_available) {
      return { data: null, error: { message: 'Agent is not available' } }
    }

    // Mark agent as busy
    await supabase
      .from('lawyers')
      .update({
        is_available: false,
        current_project_id: agentRequestId,
      })
      .eq('id', lawyerId)

    // Assign to agent request
    const { data, error } = await supabase
      .from('agent_requests')
      .update({
        lawyer_id: lawyerId,
        status: 'Assigned',
        assigned_date: new Date().toISOString(),
      })
      .eq('id', agentRequestId)
      .select()
      .single()

    // Log availability
    await supabase
      .from('agent_availability_log')
      .insert({
        lawyer_id: lawyerId,
        status: 'busy',
        reason: `Assigned to project ${agentRequestId}`,
      })

    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Complete project (agent becomes available again)
  async completeProject(agentRequestId, lawyerId) {
    // Mark agent as available
    await supabase
      .from('lawyers')
      .update({
        is_available: true,
        current_project_id: null,
      })
      .eq('id', lawyerId)

    // Increment total completed
    const { data: lawyer } = await supabase
      .from('lawyers')
      .select('total_completed')
      .eq('id', lawyerId)
      .single()
    
    if (lawyer) {
      await supabase
        .from('lawyers')
        .update({ total_completed: (lawyer.total_completed || 0) + 1 })
        .eq('id', lawyerId)
    }

    // Log availability
    await supabase
      .from('agent_availability_log')
      .insert({
        lawyer_id: lawyerId,
        status: 'available',
        reason: `Completed project ${agentRequestId}`,
      })

    // Update agent request
    const { data, error } = await supabase
      .from('agent_requests')
      .update({
        status: 'Finalized',
        client_approved_date: new Date().toISOString(),
      })
      .eq('id', agentRequestId)
      .select()
      .single()

    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Leave a review for agent
  async reviewAgent(agentRequestId, lawyerId, companyId, rating, comment) {
    const { data, error } = await supabase
      .from('agent_reviews')
      .insert({
        lawyer_id: lawyerId,
        company_id: companyId,
        agent_request_id: agentRequestId,
        rating,
        comment,
      })
      .select()
      .single()

    // Update agent rating
    if (!error) {
      const { data: reviews } = await supabase
        .from('agent_reviews')
        .select('rating')
        .eq('lawyer_id', lawyerId)

      if (reviews && reviews.length > 0) {
        const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        await supabase
          .from('lawyers')
          .update({ rating: avgRating })
          .eq('id', lawyerId)
      }
    }

    return error ? handleError(error) : { data, error: null }
  },

  // ✅ Get agent details
  async getAgentDetails(lawyerId) {
    const { data, error } = await supabase
      .from('lawyers')
      .select('*, agent_reviews(rating, comment, created_at)')
      .eq('id', lawyerId)
      .single()
    return error ? handleError(error) : { data, error: null }
  },
}